import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { VmCallPreviewRequest } from '@models';
import { VOICEMAIL_GATEWAY, VoicemailGatewayError } from '@services';

/**
 * Plays the call preview: audio the API speaks with the voice and script rules of a real call,
 * followed by the tone the carrier plays as recording starts.
 *
 * Played through an AudioContext rather than an audio element. The audio only arrives after a
 * network round trip, by which time the browser no longer counts playback as the provider's click;
 * a context resumed during the click stays allowed to play.
 */
@Injectable()
export class VoicemailAudioPreviewService implements OnDestroy {
  readonly state = signal<'idle' | 'loading' | 'playing' | 'error'>('idle');
  readonly error = signal<string | null>(null);
  readonly elapsedSeconds = signal(0);
  /** Share of the call played, 0 to 1. Null while the audio is still being prepared. */
  readonly progress = signal<number | null>(0);
  readonly elapsedLabel = computed(() => {
    const seconds = this.elapsedSeconds();
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  });
  private readonly gateway = inject(VOICEMAIL_GATEWAY);
  private context: AudioContext | null = null;
  private source: AudioBufferSourceNode | null = null;
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  private generation = 0;

  async play(request: VmCallPreviewRequest): Promise<void> {
    this.stop();
    if (typeof window === 'undefined' || !('AudioContext' in window)) {
      this.fail(
        'Audio preview is unavailable in this browser. Try another browser.'
      );
      return;
    }

    const generation = this.generation;
    this.state.set('loading');
    this.error.set(null);
    this.progress.set(null);
    try {
      const context = new AudioContext();
      this.context = context;
      // Started now, while the click still counts, and awaited once the audio is here.
      const resumed = context.resume();
      const audio = await this.gateway.getPreviewAudio(request);
      await resumed;
      if (generation !== this.generation) return;
      const buffer = await context.decodeAudioData(await audio.arrayBuffer());
      if (generation !== this.generation) return;
      this.start(context, buffer, generation);
    } catch (error: unknown) {
      if (generation !== this.generation) return;
      this.fail(
        error instanceof VoicemailGatewayError
          ? error.message
          : 'Audio preview could not play. Your changes are still here. Try again.'
      );
    }
  }

  stop(): void {
    this.generation++;
    this.clearTimer();
    if (this.source) {
      this.source.onended = null;
      try {
        this.source.stop();
      } catch {
        // Never started, or already finished.
      }
    }
    this.source = null;
    if (this.context) void this.context.close().catch(() => undefined);
    this.context = null;
    this.state.set('idle');
    this.elapsedSeconds.set(0);
    this.progress.set(0);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private start(
    context: AudioContext,
    buffer: AudioBuffer,
    generation: number
  ): void {
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    this.source = source;

    const startedAt = context.currentTime;
    this.state.set('playing');
    this.progress.set(0);
    this.elapsedTimer = setInterval(() => {
      if (generation !== this.generation) return;
      const played = context.currentTime - startedAt;
      this.elapsedSeconds.set(Math.floor(played));
      // The last stretch of the bar belongs to the tone.
      this.progress.set(Math.min(0.95, (played / buffer.duration) * 0.95));
    }, 250);

    source.onended = () => {
      if (generation !== this.generation) return;
      this.clearTimer();
      this.progress.set(0.95);
      this.playTone(generation);
    };
    source.start();
  }

  private playTone(generation: number): void {
    const context = this.context;
    if (!context) return;
    const tone = context.createOscillator();
    const volume = context.createGain();
    tone.frequency.value = 440;
    volume.gain.setValueAtTime(0.12, context.currentTime);
    volume.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.35);
    tone.connect(volume);
    volume.connect(context.destination);
    tone.onended = () => {
      if (generation !== this.generation) return;
      const elapsed = this.elapsedSeconds();
      this.stop();
      this.elapsedSeconds.set(elapsed);
      this.progress.set(1);
    };
    tone.start();
    tone.stop(context.currentTime + 0.4);
  }

  private fail(message: string): void {
    this.stop();
    this.error.set(message);
    this.state.set('error');
  }

  private clearTimer(): void {
    if (this.elapsedTimer !== null) clearInterval(this.elapsedTimer);
    this.elapsedTimer = null;
  }
}
